import type { keys } from '@confluence/storage-manager/entry-points/LocalStorageKeys.ts';
import { confluenceLocalStorageInstance as localStorage } from '@confluence/storage-manager/entry-points/storageInstance.ts';

type LocalStorageKeysMap = typeof keys;
type LocalStorageKey = keyof LocalStorageKeysMap;
type LocalStorageKeyValue = LocalStorageKeysMap[LocalStorageKey];

export type CachedContentIdTimestampTuple = [contentId: string, timestamp: number];

// The cached entity is a comma-separated list of <contentId>:<timestamp> string pairs
// Looks like this: "contentId1:1230498,contentId2:390489823,contentId3:3092480"
export const getCachedTuples = (key: LocalStorageKeyValue): CachedContentIdTimestampTuple[] => {
	try {
		const cachedContentTimestampPairs: string = localStorage.getItem(key) ?? '';

		return cachedContentTimestampPairs
			?.split(',')
			.reduce<CachedContentIdTimestampTuple[]>((acc, entry) => {
				if (!entry) return acc;

				const [contentId, timestamp] = entry.split(':');

				if (contentId && timestamp) {
					acc.push([contentId, Number(timestamp)]);
				}

				return acc;
			}, []);
	} catch {
		// ToDo: Emit operational error analytic
		return [];
	}
};

export const setCachedTuples = (
	entries: CachedContentIdTimestampTuple[],
	key: LocalStorageKeyValue,
) => {
	try {
		const filteredEntries = entries.flatMap((entry) =>
			entry[0] && entry[1] && typeof entry[0] === 'string' && typeof entry[1] === 'number'
				? [entry.join(':')]
				: [],
		);

		if (filteredEntries.length) {
			localStorage.setItem(key, filteredEntries.join(','));
		} else {
			localStorage.removeItem(key);
		}
	} catch {
		// ToDo: Emit operational analytic event
	}
};

export const getCachedTimestamp = (contentId: string, key: LocalStorageKeyValue): number => {
	const cache = getCachedTuples(key);
	const cachedEntry = cache.find(([cachedId]) => cachedId === contentId);
	const timestamp = cachedEntry?.[1] ?? 0;
	return timestamp;
};

export const removeCachedTuple = (
	contentId: string,
	key: LocalStorageKeyValue,
	/** If no condition is provided, then we remove without asking questions */
	condition?: (timestamp: number) => boolean,
) => {
	const cache = getCachedTuples(key);
	const cachedTupleIndex = cache.findIndex((entry) => entry[0] === contentId);
	const cachedTuple = cachedTupleIndex > -1 ? cache[cachedTupleIndex] : null;
	const cachedTimestamp = cachedTuple?.[1] ?? 0;
	const shouldRemove = condition ? condition(cachedTimestamp) : true;

	if (shouldRemove) {
		const cleanedCache = cache.filter(([cachedId]) => cachedId !== contentId);
		setCachedTuples(cleanedCache, key);
	}
};

export const setCachedTuple = (
	contentId: string,
	timestamp: number,
	key: LocalStorageKeyValue,
	/** If no condition is provided, then we set without asking questions */
	condition?: (timestamp: number) => boolean,
) => {
	const shouldSet = condition ? condition(timestamp) : true;

	if (shouldSet) {
		const cache = getCachedTuples(key);
		const cachedTupleIndex = cache.findIndex((entry) => entry[0] === contentId);
		const updatedEntry: CachedContentIdTimestampTuple = [contentId, timestamp];

		if (cachedTupleIndex === -1) {
			cache.push(updatedEntry);
		} else {
			cache[cachedTupleIndex] = updatedEntry;
		}

		setCachedTuples(cache, key);
	}
};
